import React, { useState } from 'react';
import { ContentCopy, CheckCircle, ExpandMore, ExpandLess } from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

const LANG_MAP = { sh: 'bash', js: 'javascript', py: 'python', curl: 'bash' };

export default function CodeBlockWithCopy({ code, language = 'text', uiVariant = 'dark' }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isLight = uiVariant === 'light';
  const lang = LANG_MAP[language] || language || 'text';
  const label = language && language !== 'text' ? language : 'code';
  const lineCount = (code || '').split('\n').length;
  const showExpand = lineCount > 12;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div
      className={`mt-2 rounded-xl overflow-hidden border shadow-xl ${
        isLight ? 'bg-white/95 border-slate-200' : 'bg-slate-950/90 border-slate-700/80'
      }`}
    >
      <div
        className={`flex justify-between items-center px-4 py-2 border-b ${
          isLight ? 'bg-white border-slate-200' : 'bg-slate-900/90 border-slate-700/80'
        }`}
      >
        <span className={`text-xs font-semibold uppercase tracking-[0.15em] font-mono ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
          {label}
        </span>
        <div className="flex items-center gap-1">
          {showExpand && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className={`flex items-center gap-1 text-xs transition-colors rounded-lg px-2 py-1 ${
                isLight ? 'text-slate-500 hover:text-amber-600' : 'text-slate-400 hover:text-amber-300'
              }`}
              aria-label={expanded ? 'Réduire' : 'Développer'}
            >
              {expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
              <span>{expanded ? 'Réduire' : 'Développer'}</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleCopy}
            className={`flex items-center gap-1.5 text-xs transition-all duration-200 rounded-lg px-2.5 py-1.5 ${
              isLight ? 'text-slate-500 hover:text-amber-600 hover:bg-slate-100' : 'text-slate-400 hover:text-amber-300 hover:bg-slate-700/70'
            }`}
            aria-label="Copier le code"
          >
            {copied ? (
              <>
                <CheckCircle className="text-emerald-400" sx={{ fontSize: 16 }} />
                <span className="text-emerald-400">Copié</span>
              </>
            ) : (
              <>
                <ContentCopy sx={{ fontSize: 16 }} />
                <span>Copier</span>
              </>
            )}
          </button>
        </div>
      </div>
      <div className={showExpand && !expanded ? 'max-h-[280px] overflow-hidden relative' : ''}>
        <SyntaxHighlighter
          language={lang === 'text' ? 'plaintext' : lang}
          style={isLight ? oneLight : oneDark}
          customStyle={{
            margin: 0,
            padding: '14px 16px',
            fontSize: '13px',
            lineHeight: 1.6,
            background: 'transparent',
            borderRadius: 0,
            fontFamily: '"JetBrains Mono", "Fira Code", ui-monospace, monospace',
          }}
          codeTagProps={{ style: { fontFamily: 'inherit' } }}
          showLineNumbers={lineCount > 3}
          PreTag="div"
        >
          {code}
        </SyntaxHighlighter>
        {showExpand && !expanded && (
          <div
            className={`absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t pointer-events-none ${
              isLight ? 'from-white via-white/80 to-transparent' : 'from-slate-950 to-transparent'
            }`}
          />
        )}
      </div>
    </div>
  );
}
